import { useCallback, useEffect, useState } from 'react';

import {
  listAdminDeals,
  type AdminDealRow,
  type AdminDealStatus,
  type PropertyType,
} from '@/lib/db/deals';

export type AdminDealsFilter = {
  status: AdminDealStatus | 'all';
  searchQuery: string;
  /** Property-type whitelist (ADR-027). Empty array = no restriction. */
  propertyType?: PropertyType[];
  /** 0-based page index (ADR-056 — server pagination, §4.10). */
  page: number;
  pageSize: number;
};

export type AdminDealsState = {
  status: 'loading' | 'success' | 'error';
  rows: AdminDealRow[];
  total: number;
  error: Error | null;
  refetch: () => void;
};

const INITIAL: Omit<AdminDealsState, 'refetch'> = {
  status: 'loading',
  rows: [],
  total: 0,
  error: null,
};

export function useAdminDeals(filter: AdminDealsFilter): AdminDealsState {
  const [state, setState] = useState<Omit<AdminDealsState, 'refetch'>>(INITIAL);
  const [tick, setTick] = useState(0);

  const { status, searchQuery, propertyType, page, pageSize } = filter;
  // Stable join key so a fresh `propertyType` array identity (parent
  // re-render) does not retrigger the fetch when the contents are unchanged.
  const propertyTypeKey = (propertyType ?? []).join('|');

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    listAdminDeals({ status, searchQuery, propertyType, pageSize, offset: page * pageSize })
      .then((result) => {
        if (cancelled) return;
        setState({
          status: 'success',
          rows: result.rows,
          total: result.total,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          rows: [],
          total: 0,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, searchQuery, propertyTypeKey, page, pageSize, tick]);

  // Refetch when the tab regains visibility — covers the back-office flow where
  // an admin edits a deal in another tab (or via the edit page) and returns to
  // the list expecting fresh data.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  return { ...state, refetch };
}
